/**
 * VirtualDeckSystem.js
 * 
 * A reusable system for implementing virtual decks in scratch card games.
 * This system creates predetermined card outcomes according to prize distribution odds,
 * shuffles them cryptographically, and allows for verifiable fairness.
 */

import { SHA256 } from 'crypto-js';

// Constants for local storage keys
const STORAGE_PREFIX = 'virtual_deck_';
const getBatchKey = (gameId) => `${STORAGE_PREFIX}${gameId}_batch`;
const getDrawnCardsKey = (gameId) => `${STORAGE_PREFIX}${gameId}_drawn_cards`;
const getSeedKey = (gameId) => `${STORAGE_PREFIX}${gameId}_seed`;
const getSaltKey = (gameId) => `${STORAGE_PREFIX}${gameId}_salt`;
const getDeckHashKey = (gameId) => `${STORAGE_PREFIX}${gameId}_deck_hash`;

/**
 * Creates a complete deck of cards with all details predetermined
 * @param {Object} gameConfig - The game configuration with prize distribution
 * @param {number} batchSize - The total number of cards in the batch
 * @returns {Array} The complete deck of cards
 */
export const createCompleteCardDeck = (gameConfig, batchSize) => {
  const completeDeck = [];
  const totalPrizeCount = calculateTotalPrizeCount(gameConfig);
  
  if (totalPrizeCount > batchSize) {
    console.error(`Error: Total prize count (${totalPrizeCount}) exceeds batch size (${batchSize})`);
    throw new Error('Invalid prize distribution: too many winners for batch size');
  }
  
  // Add winning cards according to the prize distribution
  gameConfig.bountyPool.tiers.forEach(tier => {
    tier.prizes.forEach(prize => {
      for (let i = 0; i < prize.count; i++) {
        // Convert prize amount to display format
        const prizeString = formatPrizeValue(prize.amount, gameConfig.prizeFormatting);
        
        // Create winning symbols (3 circles or whatever the game uses)
        const winningSymbols = [prizeString]; // One must be the prize value
        const otherPrizes = [...gameConfig.prizes].filter(p => p !== prizeString);
        
        // Add random other prizes as winning symbols
        while (winningSymbols.length < gameConfig.winningSymbolsCount) {
          const randomIdx = Math.floor(Math.random() * otherPrizes.length);
          const randomPrize = otherPrizes.splice(randomIdx, 1)[0];
          winningSymbols.push(randomPrize);
        }
        
        // Create grid symbols
        const totalCells = gameConfig.gridSize.rows * gameConfig.gridSize.columns;
        const gridSymbols = [];
        const winningPositions = [];
        
        // Select positions for the winning value
        const matchCount = gameConfig.matchesNeeded || 3; // Default to 3 matches
        while (winningPositions.length < matchCount) {
          const pos = Math.floor(Math.random() * totalCells);
          if (!winningPositions.includes(pos)) {
            winningPositions.push(pos);
          }
        }
        
        // Fill the grid
        for (let j = 0; j < totalCells; j++) {
          if (winningPositions.includes(j)) {
            gridSymbols.push(prizeString);
          } else {
            // Add non-matching values
            const availableForGrid = [...gameConfig.prizes].filter(p => {
              // Avoid creating unintended matches
              const currentCount = gridSymbols.filter(s => s === p).length;
              return currentCount < (matchCount - 1);
            });
            
            const randomGridIdx = Math.floor(Math.random() * availableForGrid.length);
            gridSymbols.push(availableForGrid[randomGridIdx]);
          }
        }
        
        // Add the complete winning card to the deck
        completeDeck.push({
          isWinner: true,
          prizeAmount: prize.amount,
          lotteryEntries: prize.entries || 0,
          winningSymbols: winningSymbols,
          gridSymbols: gridSymbols,
          winningPositions: winningPositions
        });
      }
    });
  });
  
  // Create non-winning cards
  const nonWinningCount = batchSize - completeDeck.length;
  for (let i = 0; i < nonWinningCount; i++) {
    // Create winning symbols
    const winningSymbols = [];
    const availablePrizes = [...gameConfig.prizes];
    
    while (winningSymbols.length < gameConfig.winningSymbolsCount) {
      const idx = Math.floor(Math.random() * availablePrizes.length);
      winningSymbols.push(availablePrizes.splice(idx, 1)[0]);
    }
    
    // Create grid symbols ensuring NO matches with winning symbols
    const gridSymbols = [];
    const totalCells = gameConfig.gridSize.rows * gameConfig.gridSize.columns;
    const matchCount = gameConfig.matchesNeeded || 3;
    
    for (let j = 0; j < totalCells; j++) {
      // Track counts to avoid unintended matches
      const symbolCounts = {};
      gridSymbols.forEach(s => {
        symbolCounts[s] = (symbolCounts[s] || 0) + 1;
      });
      
      // Filter available prizes to avoid matches
      const availableForGrid = [...gameConfig.prizes].filter(p => {
        // Don't create matches with winning symbols
        const matchesWinningSymbol = winningSymbols.includes(p);
        // Don't create enough of the same value to trigger a match
        const currentCount = symbolCounts[p] || 0;
        return currentCount < (matchCount - 1) && (!matchesWinningSymbol || currentCount < (matchCount - 2));
      });
      
      const randomGridIdx = Math.floor(Math.random() * availableForGrid.length);
      gridSymbols.push(availableForGrid[randomGridIdx]);
    }
    
    // Assign lottery tickets to non-winning cards (1-5 tickets, with 5 being the rarest)
    let lotteryEntries = 1; // Default is 1 ticket
    
    // Use a weighted distribution for lottery tickets
    // 50% chance for 1 ticket, 25% chance for 2 tickets, 15% chance for 3 tickets,
    // 7% chance for 4 tickets, 3% chance for 5 tickets
    const randomValue = Math.random() * 100;
    if (randomValue < 50) {
      lotteryEntries = 1;
    } else if (randomValue < 75) {
      lotteryEntries = 2;
    } else if (randomValue < 90) {
      lotteryEntries = 3;
    } else if (randomValue < 97) {
      lotteryEntries = 4;
    } else {
      lotteryEntries = 5;
    }
    
    // Add the complete non-winning card to the deck
    completeDeck.push({
      isWinner: false,
      prizeAmount: 0,
      lotteryEntries: lotteryEntries,
      winningSymbols: winningSymbols,
      gridSymbols: gridSymbols,
      winningPositions: []
    });
  }
  
  return completeDeck;
};

/**
 * Calculates the total number of prize cards from the game configuration
 * @param {Object} gameConfig - The game configuration
 * @returns {number} The total number of prize cards
 */
const calculateTotalPrizeCount = (gameConfig) => {
  let count = 0;
  gameConfig.bountyPool.tiers.forEach(tier => {
    tier.prizes.forEach(prize => {
      count += prize.count;
    });
  });
  return count;
};

/**
 * Formats a prize value according to the game's formatting rules
 * @param {number} amount - The prize amount
 * @param {Object} formatting - The formatting rules
 * @returns {string} The formatted prize value
 */
const formatPrizeValue = (amount, formatting = {}) => {
  const { kFormat = true, kThreshold = 1000, decimals = 0 } = formatting;
  
  if (kFormat && amount >= kThreshold) {
    const value = amount / kThreshold;
    return `${value.toFixed(decimals)}K`;
  }
  return amount.toString();
};

/**
 * Generates a secure random seed
 * @returns {string} A random seed
 */
const generateSeed = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15) + 
         Date.now().toString();
};

/**
 * Generates a secure random salt
 * @returns {string} A random salt
 */
const generateSalt = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Shuffles a deck deterministically using seed and salt
 * @param {Array} deck - The deck to shuffle
 * @param {string} seed - The random seed
 * @param {string} salt - The secret salt
 * @returns {Array} The shuffled deck
 */
const shuffleDeck = (deck, seed, salt) => {
  const shuffled = [];
  const remaining = [...deck];

  for (let i = 0; i < deck.length; i++) {
    const hash = SHA256(seed + salt + i.toString()).toString();
    // Convert hash to a number and use it as an index
    const index = parseInt(hash.substring(0, 8), 16) % remaining.length;
    shuffled.push(remaining.splice(index, 1)[0]);
  }

  return shuffled;
};

/**
 * Generates a hash commitment for the deck
 * @param {Array} deck - The shuffled deck
 * @param {string} seed - The random seed
 * @param {string} salt - The secret salt
 * @returns {string} The deck hash
 */
const generateDeckHash = (deck, seed, salt) => {
  // We don't want to hash the entire deck as it could be very large
  // Instead, hash a representation of it
  const deckRepresentation = deck.map(card => {
    return {
      isWinner: card.isWinner,
      prizeAmount: card.prizeAmount
    };
  });
  
  return SHA256(seed + salt + JSON.stringify(deckRepresentation)).toString();
};

/**
 * Creates a new batch of cards with predetermined outcomes
 * @param {string} gameId - The ID of the game
 * @param {Object} gameConfig - The game configuration
 * @param {number} batchSize - The number of cards in the batch
 * @param {number} segmentSize - The size of each segment
 * @returns {Object} The created batch
 */
export const createNewBatch = (gameId, gameConfig, batchSize = 10000, segmentSize = 100) => {
  console.log(`Creating new batch for ${gameId} with complete virtual deck approach...`);
  
  // Generate seed and salt
  const seed = generateSeed();
  const salt = generateSalt();
  
  // Create the complete deck with all card details predetermined
  const completeDeck = createCompleteCardDeck(gameConfig, batchSize);
  console.log(`Created complete deck with ${completeDeck.filter(p => p.isWinner).length} winners`);
  
  // Shuffle the deck using the seed and salt
  const shuffledDeck = shuffleDeck(completeDeck, seed, salt);
  
  // Generate the deck hash for verification
  const deckHash = generateDeckHash(shuffledDeck, seed, salt);
  
  // Calculate number of segments
  const numSegments = Math.ceil(batchSize / segmentSize);
  
  // Create segments
  const segments = [];
  for (let i = 0; i < numSegments; i++) {
    // Calculate segment range
    const startIndex = i * segmentSize;
    const endIndex = Math.min((i + 1) * segmentSize - 1, batchSize - 1);
    const segmentCardCount = endIndex - startIndex + 1;
    
    // Create segment object
    segments.push({
      id: i + 1,
      startIndex,
      endIndex,
      totalCards: segmentCardCount,
      cardsDrawn: 0,
      isComplete: false
    });
  }
  
  // Count total winners and create prize tracking
  const totalWinners = completeDeck.filter(card => card.isWinner).length;
  
  // Create prize remaining tracking
  const prizesRemaining = {};
  gameConfig.bountyPool.tiers.forEach(tier => {
    tier.prizes.forEach(prize => {
      const prizeKey = prize.amount.toString();
      prizesRemaining[prizeKey] = prize.count;
    });
  });
  
  // Calculate total prize pool
  const totalPrizePool = gameConfig.bountyPool.tiers.reduce((total, tier) => {
    return total + tier.prizes.reduce((tierTotal, prize) => {
      return tierTotal + (prize.amount * prize.count);
    }, 0);
  }, 0);
  
  // Create the batch object
  const batch = {
    id: `${gameId}_${Date.now()}`,
    gameId: gameId,
    batchNumber: getNextBatchNumber(gameId),
    totalCards: batchSize,
    cardsDrawn: 0,
    segments,
    currentSegmentIndex: 0,
    shuffledDeck,
    deckHash,
    totalWinners,
    remainingWinners: totalWinners,
    prizesRemaining,
    totalPrizePool,
    createdAt: Date.now()
  };
  
  // Store the batch in local storage
  localStorage.setItem(getBatchKey(gameId), JSON.stringify(batch));
  
  // Store seed and salt (in a real implementation, these would be kept secure)
  localStorage.setItem(getSeedKey(gameId), seed);
  localStorage.setItem(getSaltKey(gameId), salt);
  localStorage.setItem(getDeckHashKey(gameId), deckHash);
  
  return batch;
};

/**
 * Gets the current batch for a game
 * @param {string} gameId - The ID of the game
 * @returns {Object} The current batch
 */
export const getCurrentBatch = (gameId) => {
  const batchData = localStorage.getItem(getBatchKey(gameId));
  if (!batchData) {
    return null;
  }
  
  return JSON.parse(batchData);
};

/**
 * Gets the next batch number for a game
 * @param {string} gameId - The ID of the game
 * @returns {number} The next batch number
 */
const getNextBatchNumber = (gameId) => {
  const batchData = localStorage.getItem(getBatchKey(gameId));
  if (!batchData) {
    return 1;
  }
  
  const currentBatch = JSON.parse(batchData);
  return currentBatch.batchNumber + 1;
};

/**
 * Draws the next card from the batch
 * @param {string} gameId - The ID of the game
 * @param {Object} gameConfig - The game configuration
 * @returns {Object} The drawn card data
 */
export const drawNextCard = (gameId, gameConfig) => {
  // Get the current batch
  let batch = getCurrentBatch(gameId);
  
  // Check if we need to create a new batch
  if (!batch) {
    batch = createNewBatch(gameId, gameConfig);
  }
  
  // Check if the batch is depleted
  if (batch.cardsDrawn >= batch.totalCards) {
    console.log('Batch depleted, creating new batch');
    batch = createNewBatch(gameId, gameConfig);
  }
  
  // Get the current segment
  let currentSegment = batch.segments[batch.currentSegmentIndex];
  
  // Check if current segment is complete and move to next if needed
  if (currentSegment.cardsDrawn >= currentSegment.totalCards) {
    // Mark segment as complete - this would trigger verification in a real implementation
    currentSegment.isComplete = true;
    console.log(`Segment ${currentSegment.id} completed`);
    
    // Move to next segment
    batch.currentSegmentIndex++;
    
    // If we've reached the end of segments, create a new batch
    if (batch.currentSegmentIndex >= batch.segments.length) {
      console.log('All segments completed, creating new batch');
      batch = createNewBatch(gameId, gameConfig);
      currentSegment = batch.segments[0];
    } else {
      currentSegment = batch.segments[batch.currentSegmentIndex];
      console.log(`Moving to segment ${currentSegment.id}`);
    }
  }
  
  // Get the list of drawn cards
  const drawnCards = JSON.parse(localStorage.getItem(getDrawnCardsKey(gameId)) || '[]');
  
  // Calculate the index in the shuffled deck
  const deckIndex = currentSegment.startIndex + currentSegment.cardsDrawn;
  
  // Get the predetermined outcome from the shuffled deck
  const cardOutcome = batch.shuffledDeck[deckIndex];
  
  // Generate a unique card ID
  const cardId = `${batch.id}_${deckIndex}`;
  
  // Increment the cards drawn counter for both batch and segment
  batch.cardsDrawn += 1;
  currentSegment.cardsDrawn += 1;
  
  // If this is a winning card, update the remaining prizes count
  if (cardOutcome.isWinner) {
    const prizeKey = cardOutcome.prizeAmount.toString();
    batch.prizesRemaining[prizeKey] -= 1;
    batch.remainingWinners -= 1;
  }
  
  // Create the card data
  const card = {
    id: cardId,
    gameId: gameId,
    batchId: batch.id,
    batchNumber: batch.batchNumber,
    segmentId: currentSegment.id,
    deckIndex: deckIndex,
    segmentCardNumber: currentSegment.cardsDrawn,
    cardNumber: batch.cardsDrawn,
    drawnAt: Date.now(),
    isWinner: cardOutcome.isWinner,
    prizeAmount: cardOutcome.prizeAmount,
    lotteryEntries: cardOutcome.lotteryEntries,
    winningSymbols: cardOutcome.winningSymbols,
    gridSymbols: cardOutcome.gridSymbols,
    winningPositions: cardOutcome.winningPositions,
    // Include the deck hash for verification
    deckHash: batch.deckHash
  };
  
  // Add this card to the drawn cards list
  drawnCards.push(card);
  
  // Update local storage
  localStorage.setItem(getBatchKey(gameId), JSON.stringify(batch));
  localStorage.setItem(getDrawnCardsKey(gameId), JSON.stringify(drawnCards));
  
  console.log(`Drew card #${card.cardNumber}, winner: ${card.isWinner}, prize: ${card.prizeAmount}`);
  return card;
};

/**
 * Gets the list of drawn cards for a game
 * @param {string} gameId - The ID of the game
 * @returns {Array} The list of drawn cards
 */
export const getDrawnCards = (gameId) => {
  return JSON.parse(localStorage.getItem(getDrawnCardsKey(gameId)) || '[]');
};

/**
 * Gets batch statistics for a game
 * @param {string} gameId - The ID of the game
 * @returns {Object} Batch statistics
 */
export const getBatchStatistics = (gameId) => {
  const batch = getCurrentBatch(gameId);
  if (!batch) {
    return null;
  }
  
  const drawnCards = getDrawnCards(gameId);
  
  // Calculate statistics
  const winningCards = drawnCards.filter(card => card.isWinner);
  const claimedCards = drawnCards.filter(card => card.status === 'claimed');
  const totalPrizesClaimed = claimedCards.reduce((sum, card) => sum + card.prizeAmount, 0);
  
  // Calculate lottery ticket statistics
  const nonWinningCards = drawnCards.filter(card => !card.isWinner);
  const lotteryTicketCounts = {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0
  };
  
  // Count cards by lottery ticket amount
  nonWinningCards.forEach(card => {
    const tickets = card.lotteryEntries || 1;
    lotteryTicketCounts[tickets] = (lotteryTicketCounts[tickets] || 0) + 1;
  });
  
  // Calculate total lottery tickets
  const totalLotteryTickets = nonWinningCards.reduce((sum, card) => sum + (card.lotteryEntries || 1), 0);
  
  return {
    batchNumber: batch.batchNumber,
    totalCards: batch.totalCards,
    cardsDrawn: batch.cardsDrawn,
    cardsRemaining: batch.totalCards - batch.cardsDrawn,
    totalWinners: batch.totalWinners,
    remainingWinners: batch.remainingWinners,
    winnersClaimed: claimedCards.length,
    totalPrizePool: batch.totalPrizePool,
    prizesClaimed: totalPrizesClaimed,
    prizesRemaining: batch.totalPrizePool - totalPrizesClaimed,
    prizesRemainingByTier: batch.prizesRemaining,
    claimedPrizes: claimedCards.map(card => ({
      cardId: card.cardId,
      prizeAmount: card.prizeAmount,
      claimedAt: card.claimedAt
    })),
    lotteryTickets: {
      total: totalLotteryTickets,
      distribution: lotteryTicketCounts,
      averagePerCard: nonWinningCards.length > 0 ? (totalLotteryTickets / nonWinningCards.length).toFixed(2) : 0
    }
  };
};

/**
 * Updates the status of a card in the drawn cards list
 * @param {string} gameId - The ID of the game
 * @param {string} cardId - The ID of the card to update
 * @param {string} status - The new status ('drawn', 'revealed', 'claimed')
 * @returns {boolean} Whether the update was successful
 */
export const updateCardStatus = (gameId, cardId, status) => {
  try {
    const drawnCards = getDrawnCards(gameId);
    const cardIndex = drawnCards.findIndex(card => card.cardId === cardId);
    
    if (cardIndex === -1) {
      console.error(`Card ${cardId} not found in drawn cards for game ${gameId}`);
      return false;
    }
    
    // Update the card status
    drawnCards[cardIndex].status = status;
    
    // Add timestamp for claimed cards
    if (status === 'claimed') {
      drawnCards[cardIndex].claimedAt = new Date().toISOString();
    }
    
    // Save the updated drawn cards list
    localStorage.setItem(getDrawnCardsKey(gameId), JSON.stringify(drawnCards));
    
    console.log(`Updated card ${cardId} status to ${status}`);
    return true;
  } catch (error) {
    console.error(`Error updating card status: ${error}`);
    return false;
  }
};

/**
 * Verifies a card's outcome using the deck hash and seed/salt
 * @param {string} gameId - The ID of the game
 * @param {Object} card - The card to verify
 * @returns {Object} Verification result with status and details
 */
export const verifyCardOutcome = (gameId, card) => {
  // For a real implementation, the seed and salt would only be revealed
  // after the segment or batch is complete
  const seed = localStorage.getItem(getSeedKey(gameId));
  const salt = localStorage.getItem(getSaltKey(gameId));
  const storedDeckHash = localStorage.getItem(getDeckHashKey(gameId));
  
  // Get the current batch
  const batch = getCurrentBatch(gameId);
  
  // Find the segment this card belongs to
  const segment = batch.segments.find(seg => seg.id === card.segmentId);
  
  if (!segment) {
    return {
      isValid: false,
      reason: 'Segment not found'
    };
  }
  
  // Only allow verification if the segment is complete
  if (!segment.isComplete) {
    return {
      isValid: false,
      reason: 'Segment is not complete yet'
    };
  }
  
  // Verify the deck hash
  const calculatedDeckHash = generateDeckHash(batch.shuffledDeck, seed, salt);
  if (calculatedDeckHash !== storedDeckHash) {
    return {
      isValid: false,
      reason: 'Deck hash verification failed'
    };
  }
  
  // Verify this specific card
  const cardIndex = card.deckIndex;
  const verifiedCard = batch.shuffledDeck[cardIndex];
  
  if (!verifiedCard) {
    return {
      isValid: false,
      reason: 'Card not found in deck'
    };
  }
  
  // Check if the card outcome matches
  if (verifiedCard.isWinner !== card.isWinner || 
      verifiedCard.prizeAmount !== card.prizeAmount) {
    return {
      isValid: false,
      reason: 'Card outcome does not match the deck'
    };
  }
  
  return {
    isValid: true,
    verifiedCard
  };
};

/**
 * Initializes a game state from a drawn card
 * @param {Object} card - The drawn card with predetermined values
 * @returns {Object} The game state
 */
export const initializeGameFromCard = (card) => {
  // For non-winning cards, add lottery ticket icons to the grid
  let cellValues = [...card.gridSymbols]; // Create a copy to modify
  
  if (!card.isWinner && card.lotteryEntries) {
    // Add lottery ticket icons to at least 3 cells in non-winning cards
    const ticketPositions = [];
    const totalCells = cellValues.length;
    
    // Select 3 random positions for lottery ticket icons
    while (ticketPositions.length < 3) {
      const randomPos = Math.floor(Math.random() * totalCells);
      if (!ticketPositions.includes(randomPos)) {
        ticketPositions.push(randomPos);
      }
    }
    
    // Replace the values at those positions with lottery ticket icons
    ticketPositions.forEach(pos => {
      cellValues[pos] = `🎟️ x${card.lotteryEntries}`;
    });
  }
  
  // Return the game state with the updated cell values
  return {
    status: 'active',
    cellValues: cellValues,
    winningSymbols: card.winningSymbols,
    winningCells: card.winningPositions,
    revealedCells: [],
    isWinningCard: card.isWinner,
    winAmount: card.prizeAmount,
    winningSymbolsRevealed: false,
    cardId: card.id,
    gameId: card.gameId,
    batchId: card.batchId,
    batchNumber: card.batchNumber,
    cardNumber: card.cardNumber,
    lotteryTickets: card.lotteryEntries || 0,
    // Include any other game-specific state needed
  };
};
